import { useMemo } from 'react';
import { useMarketTradesStream } from '../../../hooks/useMarketTradesStream';
import { toChecksumId } from '../utils/ids';

interface UseLiveTradesParams {
    streamMarketIds: string[];
    effectiveSelectedIds: Set<string>;
}

export function useLiveTrades({
    streamMarketIds,
    effectiveSelectedIds,
}: UseLiveTradesParams) {
    const { trades: rawLiveTrades, connected } =
        useMarketTradesStream(streamMarketIds);

    const liveTrades = useMemo(() => {
        if (effectiveSelectedIds.size === 0) return rawLiveTrades;
        const filtered = new Map<
            string,
            ReturnType<typeof rawLiveTrades.get>
        >();
        for (const [marketId, trades] of rawLiveTrades) {
            if (effectiveSelectedIds.has(marketId)) {
                filtered.set(marketId, trades);
            }
        }
        return filtered as typeof rawLiveTrades;
    }, [rawLiveTrades, effectiveSelectedIds]);

    const liveProbs = useMemo(() => {
        const map = new Map<string, number>();
        for (const [marketId, tradeList] of liveTrades) {
            if (tradeList.length > 0) {
                const latest = tradeList[tradeList.length - 1];
                const oi = Number(latest.outcome_index);
                const yesProb = oi === 1 ? 1 - latest.price : latest.price;
                map.set(marketId, yesProb);
            }
        }
        return map;
    }, [liveTrades]);

    const liveVolumes = useMemo(() => {
        const map = new Map<string, number>();
        for (const [marketId, tradeList] of liveTrades) {
            let sum = 0;
            for (const t of tradeList) sum += t.base_amount;
            if (sum > 0) map.set(marketId, sum);
        }
        return map;
    }, [liveTrades]);

    const liveTraderDeltasByMarket = useMemo(() => {
        const result = new Map<
            string,
            Map<string, { buyCount: number; sellCount: number; volume: number }>
        >();
        for (const [marketId, tradeList] of liveTrades) {
            const byAccount = new Map<
                string,
                { buyCount: number; sellCount: number; volume: number }
            >();
            for (const t of tradeList) {
                const addr = toChecksumId(`${t.account}`);
                let acc = byAccount.get(addr);
                if (!acc) {
                    acc = { buyCount: 0, sellCount: 0, volume: 0 };
                    byAccount.set(addr, acc);
                }
                acc.volume += t.base_amount;
                if (t.side.toLowerCase() === 'buy') acc.buyCount++;
                else acc.sellCount++;
            }
            result.set(marketId, byAccount);
        }
        return result;
    }, [liveTrades]);

    return {
        liveTrades,
        connected,
        liveProbs,
        liveVolumes,
        liveTraderDeltasByMarket,
    };
}
